/* eslint-disable @typescript-eslint/no-explicit-any */
import SntBatteryFullIcon from "@icons/SntBatteryFullIcon";
import moment from "../constants/SensolusMoment";
import convert from "convert-units";

const GlobalUtils = {
  getFile(entityType: any, id: any, resourceType: string, fileName: string, size?: any) {
    if (!fileName) return "";

    if (fileName.includes("/")) {
      if (resourceType === "icon")
        return fileName.includes("?")
          ? fileName + "&includeMapTemplate=true"
          : fileName + "?includeMapTemplate=true";
      else {
        let sizeQuery = size ? `size=${size}` : "";
        if (sizeQuery) {
          if (fileName.includes("?")) {
            sizeQuery = "&" + sizeQuery;
          } else {
            sizeQuery = "?" + sizeQuery;
          }
        }

        return `${fileName + sizeQuery}`;
      }
    }
    const sizeQuery = size ? "?size=" + size : "";
    return (
      "/rest/file/" +
      entityType +
      "/" +
      id +
      "/" +
      resourceType +
      "/" +
      fileName +
      sizeQuery
    );
  },

  convertLongTimeToRelativePattern(elapsed: number, language: any) {
    let result = "0ms";
    let quotient = Math.floor(elapsed / 60);
    result = elapsed % 60 > 0 ? `${Math.floor(elapsed % 60)}s` : result;
    if (quotient >= 60) {
      result = quotient % 60 > 0 ? `${quotient % 60}m` : result;
      quotient = Math.floor(quotient / 60);
      if (quotient >= 24) {
        result =
          quotient % 24 > 0
            ? `${quotient % 24}${language.h_key} ${result}` // eslint-disable-line no-restricted-syntax
            : result;
        quotient = Math.floor(quotient / 24);
        result =
          quotient > 0 ? `${quotient + language.d_key} ${result}` : result; // eslint-disable-line no-restricted-syntax
      } else {
        result =
          quotient > 0 ? `${quotient + language.h_key} ${result}` : result; // eslint-disable-line no-restricted-syntax
      }
    } else {
      result = quotient > 0 ? `${quotient}m ${result}` : result;
    }
    return result;
  },

  getTotalFilterText(numberFilter: any) {
    numberFilter = numberFilter || 0;

    if (numberFilter > 10000) {
      numberFilter = Math.floor(numberFilter / 1000) + "K";
    }

    return numberFilter;
  },

  getKibanaLink(from: string, to: string, contextMap: string, contextValue: string, sort = "desc") {
    return `/kibana/app/discover#/?_g=(filters:!(),refreshInterval:(pause:!t,value:0),time:(from:${from},to:${to}))&_a=(columns:!(),filters:!(('$state':(store:appState),meta:(alias:!n,disabled:!f,key:ContextMap.${contextMap},negate:!f,params:(query:'${contextValue}'),type:phrase),query:(match_phrase:(ContextMap.${contextMap}:'${contextValue}')))),interval:auto,query:(language:kuery,query:''),sort:!(!('@timestamp',${sort})))`;
  },
  getKibanaLinkByFilter(from: string, to: string, contextMap: string, arrayValues: string[], sort = "desc") {
    const transform = arrayValues.map(
      (value) => `(match_phrase:(ContextMap.${contextMap}:'${value}'))`
    );
    const params = arrayValues.map((value) => `'${value}'`);
    const url =
      `/kibana/app/discover#/?_g=(filters:!(),refreshInterval:(pause:!t,value:0),time:(from:${from},to:${to}))&_a=(columns:!(),filters:!(('$state':(store:appState),meta:(alias:!n,disabled:!f,` +
      `key:ContextMap.${contextMap},negate:!f,params:!(${params.join(
        ","
      )}),type:phrases),query:(bool:(minimum_should_match:1,should:!(${transform.join(
        ","
      )}))))),interval:auto,query:(language:kuery,query:''),sort:!(!('@timestamp',${sort})))`;
    return url;
  },
  getDurationStr(ms: number, language: any) {
    let negative = "";
    if (ms < 0) {
      ms = ms * -1;
      negative = "-";
    }
    const d = moment.duration(ms);
    const days = Math.floor(d.asDays());
    let daysPrefix = days !== 0 ? days + (language.d_key || "d") : ""; // eslint-disable-line no-restricted-syntax
    if (daysPrefix) daysPrefix += " ";
    const hours = Math.floor(d.asHours()) % 24;
    let hoursPrefix = hours !== 0 ? hours + (language.h_key || "h") : ""; // eslint-disable-line no-restricted-syntax
    if (hoursPrefix) hoursPrefix += " ";
    return (
      negative +
      daysPrefix +
      hoursPrefix +
      moment.utc(ms).format("m") +
      (language.asset_min_key || "min") // eslint-disable-line no-restricted-syntax
    );
  },

  formatDurationWithUnit(value: any, unit: string, language: any) {
    if (value == null || isNaN(value)) {
      return value;
    }

    let ms;
    switch (unit) {
      case "SECONDS":
        ms = value * 1000;
        break;
      case "MINUTES":
        ms = value * 60 * 1000;
        break;
      case "HOURS":
        ms = value * 60 * 60 * 1000;
        break;
      case "DAYS":
        ms = value * 24 * 60 * 60 * 1000;
        break;
      case "MONTHS":
        ms = value * 30 * 24 * 60 * 60 * 1000;
        break;
      case "YEARS":
        ms = value * 365 * 24 * 60 * 60 * 1000;
        break;
      default:
        ms = value * 1000;
        break;
    }

    return this.getDurationStr(ms, language);
  },

  getDistance(distance: number, fromUnit: any, unitDistance: string) {
    let toUnit: any = "km";
    if (unitDistance === "MILES") {
      toUnit = "mi";
    }

    const toBest = (convert(convert(distance).from(fromUnit).to(toUnit)) as any)
      .from(toUnit)
      .toBest({ cutOffNumber: 1, exclude: ["cm", "mm"] });
    return Math.floor(toBest.val) + " " + toBest.unit;
  },

  renderBatteryImpact(option: any, batteryImpact: string) {
    const batteryImpacts = ["WORST", "WORSE", "BETTER", "BEST"];
    if (batteryImpact !== "NEUTRAL") {
      let color = "";
      if (batteryImpacts.indexOf(batteryImpact) < 1) {
        color = "#ef1f1b"; // red
      } else if (batteryImpacts.indexOf(batteryImpact) < 2) {
        color = "#ef8d1b"; // orange
      } else {
        color = "#4ac77c"; // orange
      }
      return batteryImpacts
        .slice(0, batteryImpacts.indexOf(batteryImpact) + 1)
        .map((item, index) => {
          return (
            <SntBatteryFullIcon
              key={index}
              size={24}
              color={color}
              className="me-2"
              style={{ marginTop: "-5px" }}
            />
          );
        });
    }
  },

  formatAccuracy(accuracyInMeters: number, unitDistance = "METRIC") {
    if (!accuracyInMeters) return "";

    if (unitDistance === "MILES") {
      const feet = Math.round(convert(accuracyInMeters).from("m").to("ft"));
      if (feet >= 5280) {
        const miles = Math.round((feet / 5280) * 10) / 10;
        return `${miles} mi`;
      }
      return `${feet} ft`;
    }

    if (accuracyInMeters >= 1000) {
      const km = Math.round((accuracyInMeters / 1000) * 10) / 10;
      return `${km} km`;
    }

    return `${Math.round(accuracyInMeters)} m`;
  },

  /**
   * Formats cellular tower information in a consistent format across the platform.
   * Format: "mcc: {mcc}, mnc: {mnc}, lac: {lac}, cell: {cell}"
   * Example: "mcc: 234, mnc: 15, lac: 58354, cell: 263701"
   */
  formatCellularTowerInfo(data: any, unknownText = "") {
    const mcc = data.mcc ?? unknownText;
    const mnc = data.net ?? unknownText;
    const lac = data.area ?? unknownText;
    const cell = data.cellId ?? unknownText;

    return `mcc: ${mcc}, mnc: ${mnc}, lac: ${lac}, cell: ${cell}`;
  },

  /**
   * Formats duration in seconds to a human-readable string format "1d 10h 11m".
   * Only includes non-zero parts (days, hours, minutes).
   * Returns "0m" for zero or empty durations.
   */
  formatDurationFromSeconds(seconds: number) {
    if (!seconds || seconds === 0) {
      return "0m";
    }

    const duration = moment.duration(seconds, "seconds");
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();

    let result = "";
    if (days) result += days + "d ";
    if (hours) result += hours + "h ";
    if (minutes) result += minutes + "m";

    return result.trim() || "0m";
  },
};

export default GlobalUtils;
